import AbstractAuthenticatedService from '@vertical-plus/vue-js-micro-service-components/lib/model/service/abstract-authenticated-service';
import BreadcrumbItemInterface from '@/model/vo/navigation/breadcrumb-item-interface';
import DuplicateTechspecTask from '@/model/task/job/duplicate-techspec-task';
import FormFieldOptionInterface from '@vertical-plus/vue-js-components/lib/model/vos/form/form-field-option-interface';
import GetTechspecTask from '@/model/task/job/get-techspec-task';
import GetTechspecTypesTask from '@/model/task/job/get-techspec-types-task';
import GetTechspecsTask from '@/model/task/job/get-techspecs-task';
import LocaleHelper from '@vertical-plus/vue-js-core/dist/util/i18n/locale-helper';
import PatchTechspecTask from '@/model/task/job/patch-techspec-task';
import PostTechspecTask from '@/model/task/job/post-techspec-task';
import Techspec from '@/model/entity/job/techspec';
import TechspecSearchConditionsInterface from '@/model/vo/job/techspec-search-conditions-interface';
import TechspecSearchResultsVo from '@/model/vo/job/techspec-search-results-vo';
import TechspecTypeSearchConditionsInterface from '@/model/vo/job/techspec-type-search-conditions-interface';
import TechspecTypeSearchResultsVo from '@/model/vo/job/techspec-type-search-results-vo';
import UpdateTechspecJobOrderTask from '@/model/task/job/update-techspec-job-order-task';

export default class TechspecService extends AbstractAuthenticatedService
{
    /**
     * Returns the breadcrumb for a techspec
     *
     * @param techspec
     * @returns
     */

    public static getBreadcrumb(techspec: Techspec): BreadcrumbItemInterface[]
    {
        return [
            {
                text: `(${ LocaleHelper.getI18n().t('general.field.client') }) ${ techspec.client?.name }`,
                to: {
                    name: 'client-view',
                    params: {
                        id: `${ techspec.client?.id }`,
                    },
                },
            },
            {
                text: `(${ LocaleHelper.getI18n().t('general.field.techspec') }) ${ techspec.name }`,
                to: {
                    name: 'techspec-view',
                    params: {
                        id: `${ techspec.id }`,
                    },
                },
                disabled: true,
            },
        ];
    }

    /**
     * Returns a readable meeting location
     *
     * @param techspec
     * @returns
     */

    public static getMeetingLocation(techspec: Techspec): string
    {
        if (!techspec.meetingLocation)
        {
            return '-';
        }

        return techspec.meetingLocation.replace('-', ' ').replace(/(^|\s)\S/g, t => t.toUpperCase());
    }

    /**
     * Returns matching techspecs
     *
     * @returns
     */

    public async findTechspecs(searchConditions?: TechspecSearchConditionsInterface): Promise<TechspecSearchResultsVo>
    {
        const task = new GetTechspecsTask();
        task.token = this.token;
        task.searchConditions = searchConditions;

        return this.runTask(task) as Promise<TechspecSearchResultsVo>;
    }

    /**
     * Loads a techspec
     *
     * @param id
     * @returns
     */

    public async loadTechspec(id: number|string): Promise<Techspec|null>
    {
        if (typeof id === 'string')
        {
            id = parseInt(id);
            if (isNaN(id))
            {
                return null;
            }
        }

        const task = new GetTechspecTask();
        task.token = this.token;
        task.id = id;

        return this.runTask(task) as Promise<Techspec>;
    }

    /**
     * Creates a techspec
     *
     * @param techspec
     * @returns
     */

    public async createTechspec(techspec: Techspec): Promise<Techspec>
    {
        const task = new PostTechspecTask();
        task.token = this.token;
        task.techspec = techspec;

        return this.runTask(task) as Promise<Techspec>;
    }

    /**
     * Updates a techspec
     *
     * @param techspec
     * @returns
     */

    public async updateTechspec(techspec: Techspec): Promise<Techspec>
    {
        const task = new PatchTechspecTask();
        task.token = this.token;
        task.techspec = techspec;

        return this.runTask(task) as Promise<Techspec>;
    }

    /**
     * Updates the order of jobs within a techspec
     *
     * @param techspecId
     * @param order
     * @returns
     */

    public async updateTechspecJobOrder(techspecId: number, order: {[key: number]: number}): Promise<void>
    {
        const task = new UpdateTechspecJobOrderTask();
        task.token = this.token;
        task.techspecId = techspecId;
        task.order = order;

        return this.runTask(task) as Promise<void>;
    }

    /**
     * Duplicates a techspec
     *
     * @param techspecId
     * @param clientId
     * @returns
     */

    public async duplicateTechspec(techspecId: number, clientId: number): Promise<number>
    {
        const task = new DuplicateTechspecTask();
        task.token = this.token;
        task.techspecId = techspecId;
        task.clientId = clientId;

        return this.runTask(task) as Promise<number>;
    }

    /**
     * Callback function for form options
     *
     * @param search
     * @param clientId
     * @param excludeMeetings
     * @returns
     */

    public async techspecFormOptionsCallback(
        search: string,
        clientId: number|null = null,
        excludeMeetings = false,
    ): Promise<FormFieldOptionInterface[]>
    {
        const conditions: TechspecSearchConditionsInterface = {
            client: clientId ? `${ clientId }` : undefined,
            searchTerm: search,
            active: true,
            order: [
                {
                    key: 'name',
                    descending: false,
                },
            ],
        };

        if (excludeMeetings)
        {
            conditions.meeting = false;
        }

        const techspecs = await this.findTechspecs(conditions);

        const options: FormFieldOptionInterface[] = [];

        for (const techspec of techspecs.results)
        {
            options.push({
                value: techspec.id,
                title: techspec.name,
                subtitle: techspec.client?.name ?? undefined,
            });
        }

        return options;
    }

    /**
     * Callback function for form titles
     *
     * @param values
     * @returns
     */

    public async techspecFormTitlesCallback(values: string[]): Promise<Record<string, string>>
    {
        const techspecs = await this.findTechspecs({
            id: values,
            order: [
                {
                    key: 'name',
                    descending: false,
                },
            ],
        });

        const options: Record<string, string> = {};

        for (const techspec of techspecs.results)
        {
            if (!techspec.id)
            {
                continue;
            }

            options[techspec.id] = techspec.name;
        }

        return options;
    }

    /**
     * Returns matching techspec types
     *
     * @returns
     */

    public async findTechspecTypes(searchConditions?: TechspecTypeSearchConditionsInterface): Promise<TechspecTypeSearchResultsVo>
    {
        const task = new GetTechspecTypesTask();
        task.token = this.token;
        task.searchConditions = searchConditions;

        return this.runTask(task) as Promise<TechspecTypeSearchResultsVo>;
    }

    /**
     * Callback function for form options
     *
     * @param search
     * @returns
     */

    public async techspecTypeFormOptionsCallback(search: string): Promise<FormFieldOptionInterface[]>
    {
        const types = await this.findTechspecTypes({
            searchTerm: search,
            enabled: true,
            order: [
                {
                    key: 'title',
                    descending: false,
                },
            ],
        });

        const options: FormFieldOptionInterface[] = [];

        for (const type of types.results)
        {
            options.push({
                value: type.id,
                title: type.title,
            });
        }

        return options;
    }

    /**
     * Callback function for form titles
     *
     * @param values
     * @returns
     */

    public async techspecTypeFormTitlesCallback(values: string[]): Promise<Record<string, string>>
    {
        const types = await this.findTechspecTypes({
            id: values,
            order: [
                {
                    key: 'title',
                    descending: false,
                },
            ],
        });

        const options: Record<string, string> = {};

        for (const type of types.results)
        {
            if (!type.id)
            {
                continue;
            }

            options[type.id] = type.title;
        }

        return options;
    }
}